import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type ProfileModuleDto,
  ProfileModuleType,
  type ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import { ConfirmDialog } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { InlineEditCard } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { formatPhoneForDisplay } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { isValidPhoneNumber, PhoneInput } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { GripVertical, Mail, Phone, Plus, Trash2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';

import type {
  ModuleEntryGovernanceProps,
  TemplateOverrideLabels,
} from '@/core/components/profile/module-entry-governance';
import {
  ModuleEntryListRowBody,
  ModuleEntryLockedHint,
  ModuleEntryOverrideHint,
  ModuleEntryPolicySlot,
} from '@/core/components/profile/module-entry-governance-ui';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useLocale } from '@/core/hooks/use-locale';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  isEntryPolicyLocked,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import type { MemberRosterRow } from '@/core/lib/profile-template-member-values-csv';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type {
  ContactInfoEntry,
  ContactInfoModuleConfig,
  ContactType,
} from '@/core/types/profile-modules';
import {
  ContactEditForm,
  getEmptyFormState,
  getFormStateFromEntry,
  type EditFormState,
} from '@/core/components/profile/modules/contact-info-edit-form';
import {
  SortableContactEditSlot,
  SortableContactItem,
} from '@/core/components/profile/modules/contact-info-sortable-item';

export { ContactType } from '@/core/types/profile-modules';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ContactInfoModuleConfigProps extends ModuleEntryGovernanceProps {
  config: ContactInfoModuleConfig;
  onChange: (config: ContactInfoModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

function reorderItems<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...items];
  const [removed] = result.splice(fromIndex, 1);
  if (removed === undefined) return result;
  result.splice(toIndex, 0, removed);
  return result;
}

function configFromItems(
  current: ContactInfoModuleConfig,
  items: ContactInfoEntry[],
): ContactInfoModuleConfig {
  return {
    ...current,
    items,
  };
}

/** Edit state: editing item at index, or adding new email/phone */
type EditingState = { type: 'add' } | { type: 'edit'; itemIndex: number };

export function ContactInfoModuleConfigComponent({
  config,
  onChange,
  structureReadOnly = false,
  contentReadOnly = false,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  showEntryOverrideHints = false,
  templateOverrideLabels,
  overrideHintMessage,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: ContactInfoModuleConfigProps) {
  const locale = useLocale();
  const phoneLabels = locale === 'de' ? de : en;
  const dict = useDictionary();
  const contactHint = dict.profiles.edit.contactInfo;
  const isMobile = useIsMobile();

  const [localConfig, setLocalConfig] =
    useState<ContactInfoModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [formState, setFormState] = useState<EditFormState>(getEmptyFormState);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const items = useMemo(() => localConfig.items ?? [], [localConfig]);

  const handleItemsChange = (newItems: ContactInfoEntry[]) => {
    if (contentReadOnly) return;
    const newConfig = configFromItems(localConfig, newItems);
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const currentModuleDto = useMemo(
    (): ProfileModuleDto => ({
      type: ProfileModuleType.CONTACT_INFO,
      order: 0,
      enabled: true,
      config: localConfig as unknown as Record<string, unknown>,
    }),
    [localConfig],
  );

  const resetEntryToTemplate = (entryId: string) => {
    const templateItems =
      (templateModule?.config?.items as ContactInfoEntry[]) ?? [];
    const templateEntry = templateItems.find((item) => item.id === entryId);
    if (!templateEntry) return;
    const { policy: _policy, ...templateData } = templateEntry;
    handleItemsChange(
      items.map((item) =>
        item.id === entryId ? { ...templateData, id: entryId } : item,
      ),
    );
  };

  const updateItem = (
    itemIndex: number,
    updates: Partial<ContactInfoEntry>,
  ) => {
    const newItems = [...items];
    const existing = newItems[itemIndex];
    if (!existing) return;
    newItems[itemIndex] = { ...existing, ...updates } as ContactInfoEntry;
    handleItemsChange(newItems);
  };

  const removeItem = (itemIndex: number) => {
    const newItems = items.filter((_, i) => i !== itemIndex);
    handleItemsChange(newItems);
    if (editing?.type === 'edit') {
      if (editing.itemIndex === itemIndex) setEditing(null);
      else if (editing.itemIndex > itemIndex) {
        setEditing({ type: 'edit', itemIndex: editing.itemIndex - 1 });
      }
    }
  };

  const startAdd = () => {
    if (contentReadOnly) return;
    setEditing({ type: 'add' });
    setFormState(getEmptyFormState());
  };

  const startEdit = (itemIndex: number) => {
    if (contentReadOnly) return;
    const entry = items[itemIndex];
    setEditing({ type: 'edit', itemIndex });
    setFormState(entry ? getFormStateFromEntry(entry) : getEmptyFormState());
  };

  const cancelEdit = () => {
    setEditing(null);
    setFormState(getEmptyFormState());
  };

  const saveEdit = () => {
    if (editing == null) return;
    const kind = formState.kind;
    let value: string;
    if (kind === 'email') {
      value = formState.emailValue.trim();
      if (!value) {
        setFormState({
          ...formState,
          valueError: 'E-Mail-Adresse ist erforderlich.',
        });
        return;
      }
      if (!EMAIL_REGEX.test(value)) {
        setFormState({
          ...formState,
          valueError: 'Bitte eine gültige E-Mail-Adresse eingeben.',
        });
        return;
      }
    } else {
      const phoneVal = formState.phoneValue.trim();
      if (!phoneVal) {
        setFormState({
          ...formState,
          valueError: 'Telefonnummer ist erforderlich.',
        });
        return;
      }
      if (!isValidPhoneNumber(phoneVal)) {
        setFormState({
          ...formState,
          valueError: 'Bitte eine gültige Telefonnummer eingeben.',
        });
        return;
      }
      value = phoneVal;
    }
    const label = formState.label.trim() || undefined;
    const newEntry: ContactInfoEntry = {
      kind,
      value,
      label,
      type: 'business' as ContactType,
    };
    if (editing.type === 'add') {
      handleItemsChange([...items, newEntry]);
    } else {
      updateItem(editing.itemIndex, { kind, value, label });
    }
    setEditing(null);
    setFormState(getEmptyFormState());
  };

  const requestRemoveItem = (itemIndex: number) => {
    setPendingRemoveIndex(itemIndex);
  };

  const confirmRemoveItem = () => {
    if (pendingRemoveIndex == null) return;
    removeItem(pendingRemoveIndex);
    setPendingRemoveIndex(null);
  };

  const pendingRemoveEntry =
    pendingRemoveIndex != null ? items[pendingRemoveIndex] : undefined;
  const pendingRemoveLabel =
    pendingRemoveEntry?.label?.trim() ||
    (pendingRemoveEntry?.kind === 'phone'
      ? formatPhoneForDisplay(pendingRemoveEntry.value ?? '')
      : pendingRemoveEntry?.value) ||
    '';

  const lastOverIdRef = useRef<string | null>(null);

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over == null) {
      lastOverIdRef.current = null;
      return;
    }
    const isNewSlot = lastOverIdRef.current !== over.id;
    const isBackToOwnSlot =
      over.id === active.id && lastOverIdRef.current === null;
    if (isNewSlot && !isBackToOwnSlot) {
      void triggerDragOverHaptic();
    }
    lastOverIdRef.current = over.id != null ? String(over.id) : null;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (structureReadOnly || contentReadOnly) return;
    const { active, over } = event;
    if (over == null || active.id === over.id) return;
    const fromIndex = items.findIndex((_, i) => `item-${i}` === active.id);
    const toIndex = items.findIndex((_, i) => `item-${i}` === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    const reordered = reorderItems(items, fromIndex, toIndex);
    handleItemsChange(reordered);
    if (editing?.type === 'edit') {
      if (editing.itemIndex === fromIndex) {
        setEditing({ type: 'edit', itemIndex: toIndex });
      } else if (
        fromIndex < editing.itemIndex &&
        toIndex >= editing.itemIndex
      ) {
        setEditing({ type: 'edit', itemIndex: editing.itemIndex - 1 });
      } else if (
        fromIndex > editing.itemIndex &&
        toIndex <= editing.itemIndex
      ) {
        setEditing({ type: 'edit', itemIndex: editing.itemIndex + 1 });
      }
    }
  };

  const sensors = useDndProfileSensors();

  const isEditingExisting = editing?.type === 'edit';
  const editingIndex = isEditingExisting ? editing.itemIndex : -1;
  const isAdding = editing?.type === 'add';

  const editCardTitle = isAdding ? 'Kontakt hinzufügen' : 'Kontakt bearbeiten';
  /** The SMS note only concerns phone numbers (DEV-543). */
  const showPublicPhoneHint =
    items.some((entry) => entry.kind === 'phone') ||
    (editing != null && formState.kind === 'phone');
  const editCardIcon =
    formState.kind === 'email' ? (
      <Mail className="h-5 w-5" />
    ) : (
      <Phone className="h-5 w-5" />
    );

  const inlineEditCard = editing ? (
    <InlineEditCard
      variant="bare"
      asSheet={isMobile}
      title={editCardTitle}
      icon={isMobile ? undefined : editCardIcon}
      onSave={saveEdit}
      onCancel={cancelEdit}
    >
      <ContactEditForm
        state={formState}
        onChange={setFormState}
        phoneLabels={phoneLabels}
      />
    </InlineEditCard>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label>Kontakt (E-Mails &amp; Telefonnummern)</Label>
        <p className="text-sm text-muted-foreground">
          Reihenfolge per Drag &amp; Drop anpassen – z. B. geschäftliche E-Mail
          und Nummer zusammen, danach private.
        </p>
        {showPublicPhoneHint && contactHint.publicPhoneHint ? (
          <p className="text-sm text-muted-foreground">
            {showEntryPolicies
              ? contactHint.publicPhoneHintTemplate
              : contactHint.publicPhoneHint}
          </p>
        ) : null}
        {items.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={() => {
              lastOverIdRef.current = null;
              lockBodyScrollDuringDrag();
              void triggerDragStartHaptic();
            }}
            onDragOver={handleDragOver}
            onDragEnd={(e) => {
              handleDragEnd(e);
              unlockBodyScrollAfterDrag();
              void triggerDragEndHaptic();
            }}
          >
            <SortableContext
              items={items.map((_, i) => `item-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {items.map((entry, index) => {
                  const entryId = entry.id ?? String(index);
                  return isEditingExisting &&
                    index === editingIndex &&
                    !isMobile ? (
                    <SortableContactEditSlot
                      key={`edit-${index}`}
                      itemIndex={index}
                    >
                      {inlineEditCard}
                    </SortableContactEditSlot>
                  ) : (
                    <SortableContactItem
                      key={`${entry.kind}-${index}-${entry.value}`}
                      entry={entry}
                      itemIndex={index}
                      onEdit={() => startEdit(index)}
                      onRemove={() => requestRemoveItem(index)}
                      structureReadOnly={structureReadOnly}
                      entryLocked={
                        contentReadOnly ||
                        (templateModule
                          ? isEntryPolicyLocked(templateModule, entryId)
                          : false)
                      }
                      showEntryPolicy={showEntryPolicies}
                      entryPolicy={
                        templateModule
                          ? resolveEntryPolicy(templateModule, entryId)
                          : undefined
                      }
                      onEntryPolicyChange={
                        onEntryPolicyChange
                          ? (mode) => onEntryPolicyChange(entryId, mode)
                          : undefined
                      }
                      policyLabels={policyLabels}
                      fieldLockedHintMessage={fieldLockedHintMessage}
                      showEntryOverrideHints={showEntryOverrideHints}
                      currentModule={currentModuleDto}
                      templateModule={templateModule}
                      entryId={entryId}
                      overrideHintMessage={overrideHintMessage}
                      templateOverrideLabels={templateOverrideLabels}
                      onResetEntry={() => resetEntryToTemplate(entryId)}
                      workspaceId={workspaceId}
                      templateId={templateId}
                      publishStatus={publishStatus}
                      memberRoster={memberRoster}
                      memberRosterLoading={memberRosterLoading}
                      hasAssignedTeams={hasAssignedTeams}
                      memberValuesDict={memberValuesDict}
                      canManageMemberValues={!contentReadOnly}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : null}

        {isAdding && !isMobile ? (
          inlineEditCard
        ) : !structureReadOnly && !contentReadOnly ? (
          <button
            type="button"
            onClick={startAdd}
            disabled={isEditingExisting}
            className={cn(
              'group flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 px-4 py-5',
              'text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary',
              'disabled:opacity-50 disabled:pointer-events-none',
            )}
            aria-label="Kontakt hinzufügen"
          >
            <Plus className="h-5 w-5" />
            <span className="font-medium">Kontakt hinzufügen</span>
          </button>
        ) : null}
        {isMobile ? inlineEditCard : null}
      </div>

      <ConfirmDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoveIndex(null);
        }}
        title="Kontakt entfernen?"
        description={
          pendingRemoveLabel ? (
            <>
              Möchtest du{' '}
              <span className="font-medium">{pendingRemoveLabel}</span> wirklich
              entfernen? Diese Aktion kann nicht rückgängig gemacht werden.
            </>
          ) : (
            'Möchtest du diesen Kontakt wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht werden.'
          )
        }
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        destructive
        onConfirm={confirmRemoveItem}
      />
    </div>
  );
}
